import { ApprovalRounded, ConstructionRounded, Diversity3Rounded, NewReleasesRounded, PermMedia, ReviewsRounded, TimelineRounded, ViewQuiltRounded } from '@mui/icons-material'
import { Box, Button, Card, CardActionArea, CardContent, Divider, Grid, Stack, Typography } from '@mui/material'
import ApiClient from '../../backend/ApiClient'
import API_URLS from '../../backend/ApiUrls'
import { IKPICard } from '../../definitions/Shared'
import TimeHelper from '../../helpers/TimeHelper'
import { useAppDispatch, useAppSelector } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import SmartCard from '../controls/cards/SmartCard'
import KPICard from '../controls/data_visualization/KPICard'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import { snackbar } from '../services/SnackbarService'

const Administrator = () => {
    const dispatch = useAppDispatch()
    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())

    const { status, response } = useQuery({
        queryKey: buildQueryKey(API_URLS.os.getContractsStatusAnalytics, TimeHelper.stringify(time)),
        queryFn: () => ApiClient.getContractsStatusAnalytics(time),
        cache: true
    })

    const underDev = () => {
        snackbar.show({
            severity: 'info',
            text: 'This functionality will be developed in future.'
        })
    }

    const defaultKPICard: IKPICard = { value: 0, df: { points: [] } }
    const contractsLive = response?.liveContracts ?? defaultKPICard
    const contractsDraft = response?.draftContracts ?? defaultKPICard
    const contractsArchived = response?.archivedContracts ?? defaultKPICard
    const uniqueMembersLive = response?.uniqueMembersLive ?? 0
    const uniqueMembersLiveGroupedByPlanType = response?.uniqueMemberGroupedByPlanType ?? null
    const fullRiskMembers = response?.fullRiskMembers ?? 0
    const sharedRiskMembers = response?.sharedRiskMembers ?? 0
    const isLoading = status === 'loading'

    return (
        <Stack spacing={3}>
            <Stack direction='row' justifyContent='space-between'>
                <Typography variant='h5'>
                    <strong>Admin&apos;s Hub</strong>
                </Typography>
                <Stack direction='row' spacing={1}>
                    <Button onClick={underDev} variant='outlined'>View Tickets</Button>
                    <Button onClick={underDev} startIcon={<NewReleasesRounded />} variant='contained'>Open Ticket</Button>
                </Stack>
            </Stack>

            <SmartCard>

                <Stack spacing={2}>
                    <Stack direction='row' spacing={1}>
                        <ConstructionRounded fontSize='large' />
                        <Typography variant='h5' fontWeight='bold'>Admin&apos;s tools</Typography>
                    </Stack>
                    <Divider />
                    <Stack display='grid' gridTemplateColumns='repeat(auto-fill, minmax(20%, 1fr))' gap={2} direction='row'>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardActionArea onClick={() => dispatch(changeRoute({ route: 'tooltip-admin' }))}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <Stack spacing={2}>
                                            <ReviewsRounded fontSize='large' color='primary' />
                                            <Stack>
                                                <Typography variant='body1'>Tooltips Manager</Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Manage all the tooltips and help messages
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardActionArea onClick={() => dispatch(changeRoute({ route: 'contracts', params: { tab: 'schemas' } }))}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <Stack spacing={2}>
                                            <ViewQuiltRounded fontSize='large' color='primary' />
                                            <Stack>
                                                <Typography variant='body1'>Contract Schema Editor</Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Edit the templates to build contracts&apos; structures
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardActionArea onClick={() => dispatch(changeRoute({ route: 'media' }))}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <Stack spacing={2}>
                                            <PermMedia fontSize='large' color='primary' />
                                            <Stack>
                                                <Typography variant='body1'>Media Manager</Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Manage all media types like PDFs, Images, Documents...
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardActionArea onClick={() => dispatch(changeRoute({ route: 'approvals' }))}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <Stack spacing={2}>
                                            <ApprovalRounded fontSize='large' color='primary' />
                                            <Stack>
                                                <Typography variant='body1'>Approvals Hub</Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Manage all approval requests for new contract, report...
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardActionArea onClick={() => dispatch(changeRoute({ route: 'groups-management' }))}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <Stack spacing={2}>
                                            <Diversity3Rounded fontSize='large' color='primary' />
                                            <Stack>
                                                <Typography variant='body1'>Approval Groups</Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Manage all the approval groups, add, remove or edit infos
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>

                    </Stack>
                </Stack>
            </SmartCard>

            <SmartCard>
                <Stack gap={2}>
                    <Stack direction='row' spacing={1} justifyContent='space-between'>
                        <Box display='flex' gap={1}>
                            <TimelineRounded fontSize='large' />
                            <Typography variant='h5' fontWeight='bold'>Analytics</Typography>
                        </Box>
                    </Stack>

                    <Divider />

                    <Grid container spacing={2}>
                        <Grid item md={3}>
                            <SmartCard>
                                <KPICard
                                    data={contractsLive}
                                    inverse
                                    isLoading={isLoading}
                                    label='Live contracts'
                                />
                            </SmartCard>
                        </Grid>
                        <Grid item md={3}>
                            <SmartCard>
                                <KPICard
                                    data={contractsDraft}
                                    isLoading={isLoading}
                                    label='Draft contracts'
                                />
                            </SmartCard>
                        </Grid>
                        <Grid item md={3}>
                            <SmartCard>
                                <KPICard
                                    data={contractsArchived}
                                    isLoading={isLoading}
                                    inverse
                                    label='Archived contracts'
                                />
                            </SmartCard>
                        </Grid>
                        <Grid item md={3}>
                            <SmartCard>
                                <KPICard
                                    data={{ value: uniqueMembersLive }}
                                    isLoading={isLoading}
                                    format='number'
                                    noChart
                                    label='Total Unique Members Live'
                                />
                            </SmartCard>
                        </Grid>

                        {/* 2nd row - Full Risk */}
                        <Grid item md={3}>
                            <SmartCard>
                                <KPICard
                                    data={{ value: fullRiskMembers }}
                                    isLoading={isLoading}
                                    format='number'
                                    noChart
                                    label='Full Risk Unique Members Live'
                                />
                            </SmartCard>
                        </Grid>
                        <Grid item md={3}>
                            <SmartCard>
                                <KPICard
                                    data={{ value: uniqueMembersLiveGroupedByPlanType?.find(item => item.programType === 'FULL_RISK' && item.planMarket === 'BCNA')?.sum ?? 0 }}
                                    isLoading={isLoading}
                                    format='number'
                                    noChart
                                    label='Full Risk BCNA'
                                />
                            </SmartCard>
                        </Grid>
                        <Grid item md={3}>
                            <SmartCard>
                                <KPICard
                                    data={{ value: uniqueMembersLiveGroupedByPlanType?.find(item => item.programType === 'FULL_RISK' && item.planMarket === 'MAPPO')?.sum ?? 0 }}
                                    isLoading={isLoading}
                                    format='number'
                                    noChart
                                    label='Full Risk MAPPO'
                                />
                            </SmartCard>
                        </Grid>
                        <Grid item md={3}>
                            <SmartCard>
                                <KPICard
                                    data={{ value: uniqueMembersLiveGroupedByPlanType?.find(item => item.programType === 'COHORT' && item.planMarket === 'COHORT')?.sum ?? 0 }}
                                    isLoading={isLoading}
                                    format='number'
                                    noChart
                                    label='Full Risk COHORT'
                                />
                            </SmartCard>
                        </Grid>

                        {/* 3rd Row - Shared Risk */}
                        <Grid item xs={3}>
                            <SmartCard>
                                <KPICard
                                    data={{ value: sharedRiskMembers }}
                                    isLoading={isLoading}
                                    format='number'
                                    noChart
                                    label='Shared Risk Unique Members Live'
                                />
                            </SmartCard>
                        </Grid>
                        <Grid item xs={3}>
                            <SmartCard>
                                <KPICard
                                    data={{ value: uniqueMembersLiveGroupedByPlanType?.find(item => item.programType === 'SHARED_RISK' && item.planMarket === 'BCNA')?.sum ?? 0 }}
                                    isLoading={isLoading}
                                    format='number'
                                    noChart
                                    label='Shared Risk BCNA'
                                />
                            </SmartCard>
                        </Grid>
                        <Grid item xs={3}>
                            <SmartCard>
                                <KPICard
                                    data={{ value: uniqueMembersLiveGroupedByPlanType?.find(item => item.programType === 'SHARED_RISK' && item.planMarket === 'MAPPO')?.sum ?? 0 }}
                                    isLoading={isLoading}
                                    format='number'
                                    noChart
                                    label='Shared Risk MAPPO'
                                />
                            </SmartCard>
                        </Grid>

                    </Grid>

                </Stack>
            </SmartCard>

        </Stack>
    )
}

export default Administrator
